/**
 * `Hl7Renderer` — the top-level component for displaying HL7 v2 messages inside the document
 * viewer. Dispatched by `DocumentRenderer` for text payloads that pass `isHl7Message`
 * (in `@ctw/shared/api/fhir/models/document`); any other text payload renders as a table or
 * as raw preformatted text instead.
 *
 * Useful spec references for anyone reading or extending this code:
 * - HL7 v2.5 segments index: https://hl7-definition.caristix.com/v2/HL7v2.5/Segments
 * - HL7 v2.5 datatypes index: https://hl7-definition.caristix.com/v2/HL7v2.5/DataTypes
 * - HL7 v2.5 user-defined tables: https://hl7-definition.caristix.com/v2/HL7v2.5/Tables
 * - MSH (Message Header) segment: https://hl7-definition.caristix.com/v2/HL7v2.5/Segments/MSH
 *
 * High-level flow:
 *
 *   rawMessage ──▶ HL7Message.parse() ──▶ Hl7SegmentTable per segment ──▶ LegacyTable per
 *                                              │                            field row
 *                                              │
 *                                              ▼
 *                                       Field column (position + name)
 *                                       Value column → renderHl7Field()
 *                                              │
 *                                              ▼
 *                                       (datatype dispatcher in
 *                                        hl7-field-renderers.tsx)
 *
 * Architectural notes:
 *
 * - **One `LegacyTable` per segment.** Each segment becomes its own card so repeating segments
 *   (NK1, OBX, AL1, …) stay visually distinct. The Set ID (field 1, when defined) is surfaced
 *   in the header so multiple instances are easy to disambiguate.
 *
 * - **Two columns per table: Field, Value.** The Field column carries both the spec position
 *   ("PID-5") and the human-readable field name ("Patient Name") in a stacked two-line cell so
 *   integrators can scan by either form. The Value column delegates to the per-datatype
 *   renderer in `renderHl7Field`.
 *
 * - **Row filtering.** Empty fields are hidden (HL7 messages are sparse and showing every
 *   undeclared position would bury the populated ones). Deprecated description fields that
 *   duplicate the inline display of an adjacent coded element are also hidden — see
 *   `REDUNDANT_DESCRIPTION_PAIRS`.
 *
 * - **Parse failure → `<ContentError />`.** The `hl7v2` library throws on truly malformed
 *   input; we catch and surface a user-friendly fallback rather than letting the error
 *   boundary catch it.
 */

import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LegacyTable, type LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
// Import via the local `./hl7` wrapper rather than `'hl7v2'` directly — the wrapper installs
// the browser `Buffer` polyfill before the library loads. See `./hl7.ts` for details.
import {
  type HL7Field,
  HL7Message,
  type HL7Segment,
} from '@ctw/shared/content/document-viewer/_internal/renderers/hl7/hl7';
import {
  getCodedInlineDisplay,
  renderHl7Field,
} from '@ctw/shared/content/document-viewer/_internal/renderers/hl7/hl7-field-renderers';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_VIEWER_TABLE_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import { memo, type RefObject, useMemo } from 'react';

export interface Hl7RendererProps {
  rawMessage: string;
  contentRef: RefObject<HTMLDivElement | null>;
}

interface Hl7FieldRow {
  /** Required by `LegacyTable`. Position is unique within a single segment. */
  key: string;
  /** Display label like `MSH-3`, `PID-5`. */
  positionLabel: string;
  /** Human-readable field name from the `hl7v2` dictionary, or empty when the field is unknown. */
  fieldName: string;
  /** The parsed `HL7Field` itself — passed through to the datatype-aware value renderer. */
  field: HL7Field;
}

/**
 * In some HL7 segments a deprecated free-text "description" field sits next to the proper
 * coded element it describes. When the codes carry inline display text (which they almost
 * always do), the description is just a redundant duplicate row in the rendered table. Hide
 * the description in that case.
 *
 * The pattern: a CWE/CE field at position N (e.g. DG1-3 = `R07.9^Chest pain^I10`) followed by
 * an ST/FT field at position N+1 (e.g. DG1-4 = `Chest pain`) which exists only for
 * pre-coded-element compatibility.
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/DG1.3 — DG1-3 Diagnosis Code
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/DG1.4 — DG1-4 Diagnosis Description
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/PR1.3 — PR1-3 Procedure Code
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/PR1.4 — PR1-4 Procedure Description
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/AL1.3 — AL1-3 Allergen Code
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/AL1.5 — AL1-5 Allergy Reaction
 */
const REDUNDANT_DESCRIPTION_PAIRS: Array<{
  segment: string;
  codedField: number;
  descriptionField: number;
}> = [
  { segment: 'DG1', codedField: 3, descriptionField: 4 },
  { segment: 'PR1', codedField: 3, descriptionField: 4 },
  { segment: 'AL1', codedField: 3, descriptionField: 5 },
];

/**
 * Fields that are technically required for the message to parse correctly but have zero value
 * to a clinical reader. Skipped at row-build time — the parser still uses them to interpret
 * the rest of the message.
 *
 * - **MSH-1 Field Separator** is always `|` (the fact that you're reading delimited HL7
 *   already implies it).
 * - **MSH-2 Encoding Characters** is almost always `^~\&` — parser plumbing, not data.
 * - **MSH-10 Message Control ID** is an opaque transmission-layer correlator (`MSG00001`,
 *   etc.) — useful for an integration engineer debugging an interface, but distracting for
 *   a clinical reader.
 * - **MSH-13 Sequence Number** and **MSH-14 Continuation Pointer** are batch-protocol
 *   plumbing — sequence numbering for ordered batch sends and fragment continuation pointers
 *   for messages split across multiple transmissions. Neither carries clinical content.
 * - **MSH-15 Accept Acknowledgment Type** and **MSH-16 Application Acknowledgment Type**
 *   describe how the receiver should reply at the protocol layer (`AL`/`NE`/`ER`/`SU` —
 *   always/never/error-only/success-only). They have no clinical content — purely an
 *   ack-handshake contract between the sending and receiving systems.
 * - **MSH-20 Alternate Character Set Handling Scheme** specifies how the receiver should
 *   switch between the character sets listed in MSH-18 — encoding plumbing for the parser.
 * - **MSH-21 Message Profile Identifier** identifies which conformance profile the message
 *   claims to follow — integration metadata for interface engineers, not clinical data.
 *
 * Note: **MSH-11 Processing ID** stays visible on purpose. Its `P`/`T`/`D` values
 * (Production / Training / Debugging) carry real reader-facing signal — a non-`P` value
 * means "this is test data" and is exactly the kind of safety-critical context a clinical
 * reader shouldn't lose.
 *
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.1
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.2
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.10
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.13
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.14
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.15
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.16
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.20
 * @see https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/MSH.21
 */
const ALWAYS_HIDDEN_FIELDS: ReadonlyArray<{ segment: string; position: number }> = [
  { segment: 'MSH', position: 1 },
  { segment: 'MSH', position: 2 },
  { segment: 'MSH', position: 10 },
  { segment: 'MSH', position: 13 },
  { segment: 'MSH', position: 14 },
  { segment: 'MSH', position: 15 },
  { segment: 'MSH', position: 16 },
  { segment: 'MSH', position: 20 },
  { segment: 'MSH', position: 21 },
];

const isAlwaysHidden = (segmentId: string, fieldPosition: number): boolean =>
  ALWAYS_HIDDEN_FIELDS.some(
    ({ segment, position }) => segment === segmentId && position === fieldPosition,
  );

/**
 * Walk the configured redundant pairs for the given segment type and return the set of field
 * positions that should be hidden from the rendered row list. A description field is hidden
 * iff (1) its segment+position matches a configured pair, (2) the corresponding coded field is
 * also present, and (3) the coded field's inline display text exactly matches the description
 * field's raw text after trim.
 *
 * Only an exact match counts — if the description carries additional text the integrator
 * intended to surface (e.g. a free-text qualifier), we keep both rows to avoid silently
 * dropping data. Comparison is case-sensitive on purpose because HL7 spec coding systems are.
 */
const positionsToHideForRedundancy = (
  segmentId: string,
  fieldsByPosition: Map<number, HL7Field>,
): Set<number> => {
  const hidden = new Set<number>();
  for (const pair of REDUNDANT_DESCRIPTION_PAIRS) {
    if (pair.segment !== segmentId) {
      continue;
    }
    const codedField = fieldsByPosition.get(pair.codedField);
    const descriptionField = fieldsByPosition.get(pair.descriptionField);
    // Both positions must be populated for a duplication to even be possible. If only one is
    // present (more common in real traffic), nothing to deduplicate — keep what we have.
    if (!(codedField && descriptionField)) {
      continue;
    }
    const codedDisplay = getCodedInlineDisplay(codedField).trim();
    const descriptionText = descriptionField.toHL7String().trim();
    if (codedDisplay && codedDisplay === descriptionText) {
      hidden.add(pair.descriptionField);
    }
  }
  return hidden;
};

/**
 * Build the row list for a segment. Two filters apply:
 *
 *   1. **Empty fields are hidden.** HL7 messages are sparse — most segments declare 20+
 *      positions but only populate a handful. Showing every undeclared position would bury the
 *      populated ones in noise. The position numbers we surface stay accurate because they
 *      come from the parser, not from row indexing.
 *   2. **Redundant description fields are hidden** when their content duplicates the coded
 *      element above them (DG1-3 vs DG1-4, etc.). See `REDUNDANT_DESCRIPTION_PAIRS`.
 */
const buildRowsForSegment = (segment: HL7Segment): Array<Hl7FieldRow> => {
  // Pre-index by position so the redundancy check can walk pairs without re-scanning. This is
  // a performance choice rather than a correctness one — for the message sizes we render
  // (≤30 segments × ≤50 fields) either approach is fine.
  const fieldsByPosition = new Map<number, HL7Field>();
  for (const field of segment.fields) {
    fieldsByPosition.set(field.position, field);
  }
  const hiddenPositions = positionsToHideForRedundancy(segment.segmentType, fieldsByPosition);

  const rows: Array<Hl7FieldRow> = [];
  for (const field of segment.fields) {
    if (field.isEmpty()) {
      continue;
    }
    // `field.isEmpty()` from the parser only catches structurally empty positions; a value of
    // pure whitespace (`PID|… ||   |…`) slips through and would otherwise produce a row whose
    // cell is rendered as `<EmptyDash />` by the field renderer (see `getRepString` in
    // hl7-field-renderers.tsx). Mirror the renderer's per-rep trimmed-emptiness check at the
    // row-builder level so the row is skipped entirely instead of surfacing a blank em-dash.
    if (field.repetitions.every((rep) => (rep.toHL7String() ?? '').trim() === '')) {
      continue;
    }
    if (hiddenPositions.has(field.position)) {
      continue;
    }
    if (isAlwaysHidden(segment.segmentType, field.position)) {
      continue;
    }
    rows.push({
      key: `${segment.segmentType}-${field.position}`,
      positionLabel: `${segment.segmentType}-${field.position}`,
      // `field.definition` is undefined for fields outside the dictionary's known segments
      // (custom Z-segments, fields beyond the spec's declared range). The empty-string fall
      // back is rendered as an em-dash by the Field column renderer below.
      fieldName: field.definition?.desc ?? '',
      field,
    });
  }
  return rows;
};

const COLUMNS: Array<LegacyTableColumn<Hl7FieldRow>> = [
  {
    title: 'Field',
    width: 240,
    headerClassName: tw`whitespace-nowrap`,
    render: (row) => (
      // Two-line cell: the segment-relative position label sits above the human-readable field
      // name, mirroring how spec readers naturally cite a field ("PID-5 / Patient Name").
      <div className={tw`flex flex-col leading-tight`}>
        <span className={tw`font-mono text-content-subtle text-xs`}>{row.positionLabel}</span>
        {row.fieldName ? (
          <span className={tw`font-medium`}>{row.fieldName}</span>
        ) : (
          <span className={tw`text-content-subtle`}>—</span>
        )}
      </div>
    ),
  },
  {
    title: 'Value',
    headerClassName: tw`whitespace-nowrap`,
    // `align-bottom` on the cell flips the value's vertical alignment so it lines up with the
    // bottom row of the Field cell — i.e. the human-readable field name ("Patient Name")
    // rather than the position label above it ("PID-5"). The label is the more natural
    // reading anchor; aligning to the code instead would make the value's baseline float
    // above its description.
    className: tw`align-bottom`,
    render: (row) => renderHl7Field(row.field),
  },
];

interface Hl7SegmentTableProps {
  segment: HL7Segment;
  segmentIndex: number;
}

/**
 * Read the segment's Set ID (position 1) when the segment defines one. NK1, OBX, AL1, DG1,
 * GT1, IN1, PR1, NTE, and similar repeating segments declare position 1 as "Set ID - <type>";
 * surfacing the value disambiguates multiple instances in the same message ("OBX · Set 1",
 * "OBX · Set 2", …) without requiring callers to count.
 *
 * Set ID convention: each repeating segment in HL7 v2 is preceded by a sequential index — see
 * the per-segment definitions, e.g. https://hl7-definition.caristix.com/v2/HL7v2.5/Fields/OBX.1
 * (OBX.1 — Set ID).
 */
const readSegmentSetId = (segment: HL7Segment): string | undefined => {
  try {
    const firstField = segment.field(1);
    if (!firstField.definition?.desc?.toLowerCase().startsWith('set id')) {
      return undefined;
    }
    const value = firstField.toHL7String()?.trim();
    return value ? value : undefined;
  } catch {
    return undefined;
  }
};

const Hl7SegmentTable = ({ segment, segmentIndex }: Hl7SegmentTableProps) => {
  const rows = buildRowsForSegment(segment);
  if (rows.length === 0) {
    return null;
  }

  const segmentName = segment.definition?.desc;
  const setId = readSegmentSetId(segment);
  // Disambiguate repeating segments (e.g. multiple NK1 or OBX rows in one message) so each
  // table has a stable id even when the segment type repeats.
  const tableId = `hl7-${segment.segmentType.toLowerCase()}-${segmentIndex}`;
  const segmentLabel = segmentName
    ? `${segment.segmentType}${setId ? ` #${setId}` : ''} — ${segmentName}`
    : `${segment.segmentType}${setId ? ` #${setId}` : ''}`;

  return (
    <section className={tw`flex flex-col gap-2`}>
      <header className={tw`flex flex-col`}>
        {segmentName && (
          <h3 className={tw`font-semibold text-base leading-tight`}>{segmentName}</h3>
        )}
        <span className={tw`pl-0.5 font-mono text-content-subtle text-sm leading-none`}>
          {segment.segmentType}
          {setId && <span className={tw`ml-0.5 text-content-subtle text-xs`}>#{setId}</span>}
        </span>
      </header>
      <LegacyTable
        id={tableId}
        testId={DOCUMENT_VIEWER_TABLE_TEST_ID}
        title={segmentLabel}
        titleVisuallyHidden={true}
        className={tw`!border w-full max-w-full rounded-md border-background-subtle`}
        records={rows}
        columns={COLUMNS}
        spacing="tight"
        showTableHead={true}
        emptyMessage="No populated fields in this segment."
      />
    </section>
  );
};

interface ParseResult {
  message: HL7Message | null;
  error: Error | null;
}

/*
 * HL7 v2 mandates `\r` (0x0D) as the segment terminator (§2.A.85.4). Real-world messages
 * routinely arrive over LF-normalizing transports (text editors, git autocrlf, copy/paste
 * through OS clipboards, JSON-encoded payloads) which silently rewrite `\r` → `\n` or
 * `\r\n` → `\n`. The `hl7v2` parser is spec-strict and treats a CR-less input as one giant
 * MSH segment with hundreds of fields — the visible symptom is MSH positions past MSH-21
 * (which is the end of the MSH spec) rendering with empty field names. Normalizing here
 * keeps the renderer robust regardless of the binary's lineage. The order matters: collapse
 * `\r\n` first so we don't end up with `\r\r`.
 */
const normalizeHl7SegmentTerminators = (rawMessage: string): string =>
  rawMessage.replace(/\r\n|\n/g, '\r');

const tryParseMessage = (rawMessage: string): ParseResult => {
  try {
    const message = new HL7Message();
    message.parse(normalizeHl7SegmentTerminators(rawMessage));
    return { message, error: null };
  } catch (error) {
    return {
      message: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
};

export const Hl7Renderer = memo(({ rawMessage, contentRef }: Hl7RendererProps) => {
  const { message, error } = useMemo(() => tryParseMessage(rawMessage), [rawMessage]);

  if (error || !message) {
    return (
      <ContentError
        title="Unable to parse this HL7 v2 message."
        className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" />.
      </ContentError>
    );
  }

  return (
    <div
      ref={contentRef}
      data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
      className={tw`flex w-full flex-col gap-4 overflow-auto p-5`}
    >
      {message.segments.map((segment, segmentIndex) => (
        <Hl7SegmentTable
          // biome-ignore lint/suspicious/noArrayIndexKey: stable list, items are not reordered
          key={`${segment.segmentType}-${segmentIndex}`}
          segment={segment}
          segmentIndex={segmentIndex}
        />
      ))}
    </div>
  );
});

Hl7Renderer.displayName = 'Hl7Renderer';
